import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Card, IconButton, Typography } from '@mui/material';
import React from 'react';
import { formatDate, formatUserName } from '../../utils/formatUtils'; // Import from shared utils
/**
 * Grid item component for displaying folders in grid view
 * 
 * @param {Object} props Component props
 * @param {Object} props.folder The folder object to display
 * @param {Function} props.onFolderClick Handler for when the folder is clicked
 * @param {Function} props.onMenuOpen Handler for when the context menu is opened
 */
const FolderGridItem = ({ folder, onClick, onMenuOpen, showPath }) => {
    const handleClick = () => {
        if (onClick) {
            onClick(folder);
        }
    };

    const handleMenuClick = (event) => {
        // Add debug logging
        console.log("Menu button clicked for:", folder.name);
        
        // Important: Stop propagation to prevent folder click
        event.stopPropagation();
        event.preventDefault();
        
        if (onMenuOpen) {
            onMenuOpen(event, folder);
        } else {
            console.error("onMenuOpen not provided to FolderListItem");
        }
    };

    // Get item count (if available)
    const itemCount = folder.itemCount || (folder.children ? folder.children.length : 0);

    // Get last updated by (if available)
    const updatedBy = folder.updatedBy || 'Unknown';

    // Get folder path for display in search results
    const displayPath = showPath && folder.path ? folder.path : null;
    
    return (
        <Card 
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer', // Add this to make it clear it's clickable
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 3,
                }
            }}
            onClick={handleClick}
            className='file-folder-item'
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <FolderIcon
                    sx={{
                        fontSize: '2rem',
                        color: 'primary.main'
                    }}
                />
                <IconButton
                    size="small"
                    className="file-folder-action-menu-button" 
                    onClick={handleMenuClick}
                    data-testid={`folder-menu-${folder.name}`}
                    aria-label="more options"
                    className="menu-button"
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>
            
            <Typography
                className="file-folder-name"
                variant="body2"
                noWrap
                sx={{
                    fontWeight: 500,
                    mt: 1
                }}
            >
                {folder.name}
            </Typography>

            {/* Display path in search results */}
            {showPath && displayPath && (
                <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    noWrap
                    sx={{ mb: 1 }}
                >
                    <FolderOpenIcon 
                        sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'text-bottom' }} 
                    />
                    {displayPath}
                </Typography>
            )}
            
            <Box sx={{ mt: 'auto', pt: 2 }}>
                <Box className="file-folder-updated-by"  sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <AccessTimeIcon 
                                                  
                        sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} 
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDate(folder.updatedAt)}
                    </Typography>
                </Box>
                
                <Box className="file-folder-updated-at" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <PersonIcon 
                        sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} 
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {formatUserName(updatedBy)}
                    </Typography>
                </Box>
                
                <Box className="folder-file-details" sx={{ display: 'flex', alignItems: 'center' }}>
                    <FolderZipIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                        {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

export default FolderGridItem;